"use client"

/**
 * One composer, two hosts. The provider carries everything
 * `useQuestionComposer` produced so the builder and inspector can be dropped
 * into any container without a prop chain between them.
 */

import * as React from "react"

import type { QuestionComposerApi } from "./use-question-composer"

const QuestionComposerContext = React.createContext<QuestionComposerApi | null>(
  null
)

export function QuestionComposerProvider({
  value,
  children,
}: {
  value: QuestionComposerApi
  children: React.ReactNode
}) {
  return (
    <QuestionComposerContext.Provider value={value}>
      {children}
    </QuestionComposerContext.Provider>
  )
}

export function useQuestionComposerContext(): QuestionComposerApi {
  const value = React.useContext(QuestionComposerContext)
  if (!value) {
    throw new Error(
      "Question composer fields must be rendered inside <QuestionComposerProvider>."
    )
  }
  return value
}

export type QuestionComposerInspectorSurface = "card" | "secondary-panel"

type QuestionComposerLayoutValue = {
  pageScroll: boolean
  inspectorSurface: QuestionComposerInspectorSurface
}

const QuestionComposerLayoutContext =
  React.createContext<QuestionComposerLayoutValue>({
    pageScroll: false,
    inspectorSurface: "card",
  })

export function QuestionComposerLayoutProvider({
  pageScroll = false,
  inspectorSurface = "card",
  children,
}: {
  pageScroll?: boolean
  inspectorSurface?: QuestionComposerInspectorSurface
  children: React.ReactNode
}) {
  return (
    <QuestionComposerLayoutContext.Provider
      value={{ pageScroll, inspectorSurface }}
    >
      {children}
    </QuestionComposerLayoutContext.Provider>
  )
}

export function useQuestionComposerPageScroll() {
  return React.useContext(QuestionComposerLayoutContext).pageScroll
}

export function useQuestionComposerInspectorSurface() {
  return React.useContext(QuestionComposerLayoutContext).inspectorSurface
}
